import { useState, useEffect, type ReactNode } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './msalConfig';
import { setMsalInstance } from '../services/api';

const msalInstance = new PublicClientApplication(msalConfig);

// Share the MSAL instance with the API client so it can acquire tokens
setMsalInstance(msalInstance);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    msalInstance
      .initialize()
      .then(() => {
        // Handle redirect promise after initialization
        msalInstance
          .handleRedirectPromise()
          .then((response) => {
            if (response && response.account) {
              msalInstance.setActiveAccount(response.account);
            } else {
              // If no redirect response, set active account from cache
              const accounts = msalInstance.getAllAccounts();
              if (accounts.length > 0) {
                msalInstance.setActiveAccount(accounts[0]);
              }
            }
          })
          .catch((error) => {
            console.error('MSAL redirect error:', error);
          })
          .finally(() => {
            setIsInitialized(true);
          });

        // Listen for sign-in events to set active account
        msalInstance.addEventCallback((event) => {
          if (
            event.eventType === EventType.LOGIN_SUCCESS &&
            event.payload &&
            'account' in event.payload &&
            event.payload.account
          ) {
            msalInstance.setActiveAccount(event.payload.account);
          }
        });
      })
      .catch((error) => {
        console.error('MSAL initialization error:', error);
        setIsInitialized(true);
      });
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="font-body text-text-secondary">Initializing...</p>
        </div>
      </div>
    );
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

export { msalInstance };
